import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { RegionProvider } from "@/contexts/RegionContext";
import Index from "./pages/Index";

// Defer heavy Radix providers off the critical path — they aren't needed for LCP.
const TooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then((m) => ({ default: m.TooltipProvider })),
);
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Services = lazy(() => import("./pages/Services"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SaudiArabia = lazy(() => import("./pages/SaudiArabia"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Research = lazy(() => import("./pages/Research"));
const EmailSetup = lazy(() => import("./pages/EmailSetup"));

const queryClient = new QueryClient();

/** Mount non-critical Radix providers (tooltip + toasts) after first paint. */
const DeferredProviders = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const idle =
      (window as any).requestIdleCallback ||
      ((cb: () => void) => setTimeout(cb, 200));
    const id = idle(() => setReady(true));
    return () => {
      const cancel =
        (window as any).cancelIdleCallback ||
        ((h: number) => clearTimeout(h));
      cancel(id);
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
      </TooltipProvider>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <RegionProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/services" element={<Services />} />
              <Route path="/sa" element={<SaudiArabia />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/research/gcc-clinical-nutrition-report-2026" element={<Research />} />
              <Route path="/admin/email-setup" element={<EmailSetup />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <DeferredProviders />
        </BrowserRouter>
      </RegionProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
